import { useEffect } from "react";
import { useNotify } from "react-admin";
import { useSelector } from "react-redux";
import type { AuthManager } from "../auth/Auth";
import permissionNames from "../auth/access-control/permissionNames";
import { AppState } from "../types";
import { addFleetManagerHubReconnectHandler } from "./fleetManagerHubReconnectHandlers";

type DisconnectedDrivingNotification = {
  vehicleId: number;
  vehicleName: string;
  driverId: number | null;
  driverName: string | null;
};

export const useDisconnectedDrivingNotifications = (authProvider: AuthManager) => {
  const { hub } = useSelector((state: AppState) => state.fleetManagerHub);
  const notify = useNotify();

  useEffect(() => {
    if (!hub) {
      return;
    }

    let active = true;
    let permitted = false;
    const subscribe = () =>
      hub.invoke("SubscribeToDisconnectedDrivingUpdates").catch(console.error);
    const onRecorded = (event: DisconnectedDrivingNotification) => {
      if (active && permitted) {
        notify(
          `Disconnected driving recorded - Vehicle: ${event.vehicleName}, Driver: ${event.driverName ?? "Not attributed"}`,
          { type: "info" }
        );
      }
    };
    const onReconnected = () => {
      if (active && permitted) {
        void subscribe();
      }
    };

    hub.on("OnDisconnectedDrivingRecorded", onRecorded);
    const removeReconnectHandler = addFleetManagerHubReconnectHandler(
      hub,
      onReconnected
    );
    void authProvider.getPermissions({})
      .then((permissions) => {
        if (!active || !permissions.actionPermissions?.[permissionNames.hos.eldDisconnections.main]) {
          return;
        }
        permitted = true;
        void subscribe();
      })
      .catch(console.error);

    return () => {
      active = false;
      hub.off("OnDisconnectedDrivingRecorded", onRecorded);
      removeReconnectHandler();
      if (permitted) {
        hub.invoke("UnsubscribeFromDisconnectedDrivingUpdates").catch(console.error);
      }
    };
  }, [authProvider, hub, notify]);
};
